import React, { useEffect } from 'react';
import { Pane, useMap } from 'react-leaflet';
import type { GeoJSON as GeoJSONType } from '../../lib/types';

// Pane for data-layer tiles clipped to the shed (canopy height etc.). Sits
// between the basemap's tilePane (200) and the overlayPane (400), so the
// clipped tiles render above satellite imagery but below the dashed supply
// shed boundary GeoJSON.
const DEFAULT_PANE_NAME = 'supply-shed-clip-pane';
const DEFAULT_PANE_Z_INDEX = 300;

/**
 * Continuously maintains `clip-path: path(evenodd, ...)` on the named pane so
 * that anything rendered into the pane is visually clipped to the supply shed
 * polygon — including any holes (e.g. the Yosemite wilderness gap in the
 * 90-min Sierra Nevada shed). Uses SVG path syntax with the evenodd fill rule
 * so subsequent rings after the outer one punch holes through.
 *
 * Coordinates come from `map.latLngToLayerPoint`, which is stable during pan
 * (the outer mapPane translates as a whole) — so we only recompute on zoom
 * events and viewreset. The `zoom` event fires on every frame of the zoom
 * animation, keeping the clip tight against the tiles.
 *
 * Assumes a GeoJSON Polygon (possibly with holes). MultiPolygon is not
 * supported — it would need one path per outer/inner-ring set.
 */
const ClipPathController: React.FC<{
  boundary: GeoJSONType.Polygon | null;
  paneName: string;
}> = ({ boundary, paneName }) => {
  const map = useMap();

  useEffect(() => {
    const pane = map.getPane(paneName);
    if (!pane) return;

    if (!boundary) {
      pane.style.clipPath = '';
      return;
    }

    const rings = boundary.coordinates;
    const apply = () => {
      const subpaths = rings.map(ring =>
        ring
          .map(([lng, lat], i) => {
            const p = map.latLngToLayerPoint([lat, lng]);
            return `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`;
          })
          .join(' ') + ' Z',
      );
      pane.style.clipPath = `path(evenodd, "${subpaths.join(' ')}")`;
    };

    apply();
    map.on('zoom', apply);
    map.on('zoomend', apply);
    map.on('viewreset', apply);
    return () => {
      map.off('zoom', apply);
      map.off('zoomend', apply);
      map.off('viewreset', apply);
    };
  }, [map, boundary, paneName]);

  return null;
};

interface SupplyShedClipPaneProps {
  boundary: GeoJSONType.Polygon | null;
  children: React.ReactNode;
  /**
   * Unique pane name. Each mounted instance must use a distinct name —
   * Leaflet panes are keyed globally on the map. Defaults to the data-tile
   * pane at z=300; callers layering *behind* the data tiles (e.g. an
   * in-shed basemap swap) should pass their own name and a lower zIndex.
   */
  paneName?: string;
  /** Leaflet pane z-index. Default 300 (data tiles, above basemap, below overlayPane). */
  zIndex?: number;
}

/**
 * Wraps children in a Leaflet pane whose contents are clipped to the supply
 * shed boundary. Intended for dynamic-tile layers (Earthscale et al.) that
 * render across their dataset's full extent — the clip makes them feel
 * shed-scoped like the precomputed PNG overlays.
 *
 * Usage:
 *   <SupplyShedClipPane boundary={boundary}>
 *     <TileLayer url={...} />
 *   </SupplyShedClipPane>
 */
export const SupplyShedClipPane: React.FC<SupplyShedClipPaneProps> = ({
  boundary,
  children,
  paneName = DEFAULT_PANE_NAME,
  zIndex = DEFAULT_PANE_Z_INDEX,
}) => (
  <Pane name={paneName} style={{ zIndex }}>
    {children}
    <ClipPathController boundary={boundary} paneName={paneName} />
  </Pane>
);
